import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrial } from '../context/TrialContext';
import { ShoppingCart, FileText, TrendingDown, TrendingUp, Package, Users, Calendar, Crown } from 'lucide-react';

/**
 * PlanUsagePanel
 * --------------
 * Tarjeta resumen del uso del ciclo actual: ventas, facturas, compras,
 * cobros, pagos y usuarios. Pensada para colgarla del Dashboard.
 *
 * Lee todo del TrialContext (RPC get_org_usage + max_users).
 */
const CATEGORIES = [
  { key: 'sales',       label: 'Ventas',     icon: ShoppingCart, color: 'mint' },
  { key: 'invoices',    label: 'Facturas',   icon: FileText,     color: 'graphite' },
  { key: 'purchases',   label: 'Compras',    icon: Package,      color: 'graphite' },
  { key: 'collections', label: 'Cobros',     icon: TrendingUp,   color: 'mint' },
  { key: 'payments',    label: 'Pagos',      icon: TrendingDown, color: 'graphite' },
];

const formatDate = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })
    : null;

const barColor = (percentage, reached) => {
  if (reached) return 'bg-red-500';
  if (percentage >= 85) return 'bg-orange-500';
  if (percentage >= 60) return 'bg-amber-400';
  return 'bg-[var(--gst-mint-500)]';
};

export default function PlanUsagePanel({ className = '' }) {
  const navigate = useNavigate();
  const { currentPlan, usageMap, usersLimit, usage, loadingTrial } = useTrial();

  if (loadingTrial) return null;
  if (!currentPlan) return null;

  const cycleEndStr = formatDate(usage?.cycle_end);
  const cycleStartStr = formatDate(usage?.cycle_start);

  return (
    <div className={`rounded-2xl border border-[var(--gst-border-strong)] bg-[var(--surface)] p-4 sm:p-5 shadow-sm ${className}`}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Crown size={14} className="text-[var(--gst-mint-600)]" />
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-500)]">
              Plan {currentPlan.name}
            </span>
          </div>
          <h3 className="text-[15px] font-black text-[var(--gst-graphite-900)]">
            Uso del ciclo actual
          </h3>
          {cycleStartStr && cycleEndStr && (
            <p className="text-[11px] text-[var(--gst-graphite-500)] mt-0.5 flex items-center gap-1">
              <Calendar size={11} />
              {cycleStartStr} → {cycleEndStr}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/sistema/gestion-plan')}
          className="text-[10px] font-black uppercase tracking-[0.14em] text-[var(--gst-mint-700)] hover:underline"
        >
          Cambiar plan →
        </button>
      </div>

      {/* Grid de categorías */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        {CATEGORIES.map(({ key, label, icon: Icon }) => {
          const meta = usageMap?.[key];
          if (!meta) return null;
          const percentage = meta.unlimited ? 0 : meta.percentage;
          return (
            <div
              key={key}
              className={`rounded-xl border p-3 ${
                meta.reached
                  ? 'border-red-200 bg-red-50'
                  : percentage >= 85
                  ? 'border-orange-200 bg-orange-50/60'
                  : 'border-[var(--gst-border)] bg-[var(--gst-cream-50)]'
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-wider text-[var(--gst-graphite-600)] flex items-center gap-1.5">
                  <Icon size={11} />
                  {label}
                </span>
                {meta.unlimited && (
                  <span className="text-[9px] font-black uppercase tracking-wider text-[var(--gst-mint-700)]">
                    ∞
                  </span>
                )}
              </div>
              <div className="text-[15px] font-black text-[var(--gst-graphite-900)] leading-tight">
                {meta.used.toLocaleString('es-AR')}
                {!meta.unlimited && (
                  <span className="text-[11px] font-bold text-[var(--gst-graphite-500)]">
                    {' '}/ {meta.limit.toLocaleString('es-AR')}
                  </span>
                )}
              </div>
              {!meta.unlimited && (
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--gst-border)]">
                  <div
                    className={`h-full ${barColor(percentage, meta.reached)} transition-all`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              )}
              {meta.reached && (
                <p className="text-[10px] font-bold text-red-600 mt-1.5">Límite alcanzado</p>
              )}
            </div>
          );
        })}

        {/* Usuarios */}
        <div
          className={`rounded-xl border p-3 ${
            usersLimit?.reached
              ? 'border-red-200 bg-red-50'
              : usersLimit?.percentage >= 85
              ? 'border-orange-200 bg-orange-50/60'
              : 'border-[var(--gst-border)] bg-[var(--gst-cream-50)]'
          }`}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-[var(--gst-graphite-600)] flex items-center gap-1.5">
              <Users size={11} />
              Usuarios
            </span>
            {usersLimit?.unlimited && (
              <span className="text-[9px] font-black uppercase tracking-wider text-[var(--gst-mint-700)]">
                ∞
              </span>
            )}
          </div>
          <div className="text-[15px] font-black text-[var(--gst-graphite-900)] leading-tight">
            {(usersLimit?.used ?? 0).toLocaleString('es-AR')}
            {!usersLimit?.unlimited && (
              <span className="text-[11px] font-bold text-[var(--gst-graphite-500)]">
                {' '}/ {(usersLimit?.limit ?? 0).toLocaleString('es-AR')}
              </span>
            )}
          </div>
          {!usersLimit?.unlimited && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--gst-border)]">
              <div
                className={`h-full ${barColor(usersLimit?.percentage || 0, usersLimit?.reached)} transition-all`}
                style={{ width: `${usersLimit?.percentage || 0}%` }}
              />
            </div>
          )}
          {usersLimit?.reached && (
            <p className="text-[10px] font-bold text-red-600 mt-1.5">No podés sumar más usuarios</p>
          )}
        </div>
      </div>

      <p className="text-[10px] text-[var(--gst-graphite-500)] leading-relaxed">
        Los contadores se reinician cada mes el día de tu vencimiento.
        Si alcanzás un límite, cambiá de plan o esperá el próximo ciclo.
      </p>
    </div>
  );
}
